import React, { useMemo, useState } from "react";
import { TAX_TOOLS, type TaxToolId } from "../types";
import ScorpPanel from "./ScorpPanel";
import ReconstructPanel from "./ReconstructPanel";

type Props = {
  projectId: string;
  folderSelected: boolean;
  projectName?: string;
};

/**
 * Tax Toolbox — the sibling section to the FDD Toolbox.
 *
 * Sidebar and panel chrome deliberately match FDDToolbox so the two read as
 * the same product.
 */
const TaxToolbox: React.FC<Props> = ({ projectId, folderSelected, projectName }) => {
  const [activeTool, setActiveTool] = useState<TaxToolId>(TAX_TOOLS[0].id);

  const tool = useMemo(
    () => TAX_TOOLS.find((t) => t.id === activeTool) ?? TAX_TOOLS[0],
    [activeTool]
  );

  return (
    <div style={{ marginTop: 16, display: "flex", gap: 16, alignItems: "stretch" }}>
      {/* Sidebar */}
      <aside
        style={{
          width: 220,
          flexShrink: 0,
          border: "1px solid var(--theme-border-strong)",
          borderRadius: 12,
          background: "var(--theme-tint-7)",
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div style={{ fontWeight: 700, color: "var(--theme-ink)", padding: "4px 6px", fontSize: 13 }}>
          Tax Tools
        </div>
        {TAX_TOOLS.map((t) => {
          const isActive = t.id === activeTool;
          const coming = t.comingSoon === true;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTool(t.id)}
              style={{
                background: isActive ? "var(--theme-accent)" : "#ffffff",
                color: isActive ? "#fff" : coming ? "#6b7280" : "var(--theme-ink)",
                border: `1px solid ${isActive ? "var(--theme-accent)" : "var(--theme-border-strong)"}`,
                borderRadius: 8,
                padding: "10px 12px",
                cursor: "pointer",
                textAlign: "left",
                fontWeight: isActive ? 700 : 500,
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>{t.icon}</span>
              <span style={{ flex: 1 }}>{t.name}</span>
              {coming && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    padding: "2px 6px",
                    borderRadius: 999,
                    background: isActive ? "rgba(255,255,255,0.25)" : "#fef9c3",
                    color: isActive ? "#fff" : "#854d0e",
                    textTransform: "uppercase",
                  }}
                >
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </aside>

      {/* Main area */}
      <main
        style={{
          flex: 1,
          border: "1px solid var(--theme-border-strong)",
          borderRadius: 12,
          background: "#ffffff",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              color: "var(--theme-ink)",
              fontSize: 18,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 22 }}>{tool.icon}</span>
            {tool.name}
          </h3>
          <div style={{ fontSize: 13, color: "var(--theme-ink-soft)", marginTop: 4 }}>{tool.description}</div>
        </div>

        {activeTool === "reconstruct" ? (
          <ReconstructPanel projectId={projectId} projectName={projectName} />
        ) : activeTool === "scorp" ? (
          <ScorpPanel projectId={projectId} projectName={projectName} />
        ) : (
          <section
            style={{
              border: "1px solid #f5d97e",
              borderRadius: 10,
              background: "#fffbeb",
              padding: 32,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 32 }}>{tool.icon}</div>
            <div style={{ fontWeight: 700, color: "#854d0e", fontSize: 15 }}>
              {tool.name} isn't wired up yet
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default TaxToolbox;
